import { useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { UseGetReturn } from 'restful-react'
import { IShipperRatesSortByEnum } from 'types/graphqlTypes'
import {
  GetShipperRatesQueryParams,
  ShipperRateObject,
  ShipperRatesResponse,
  useGetShipperRates,
  UseGetShipperRatesProps,
} from 'apps/marketplace/apiTypes/shipperRates'
import { getLoadType } from 'apps/marketplace/pages/awarded-lanes/utils/getLoadType'
import { capitalize } from 'utils/capitalize'
import { INameSearchResult } from '../../api/search'

export interface ShipperRateObjectAugmented extends ShipperRateObject {
  id: number
  loadType: string
  monthDuration: number
  pickupCity?: string
  deliveryCity?: string
  lane: string
  salesRepName: string
}

export enum ShipperRateSortFields {
  normalizedVolume = 'normalized_volume',
  startDate = 'start_date',
  endDate = 'end_date',
}

/**
 * Hook to get Awarded Lanes, a subset of Shipper Rates.  It also calculates additional
 * attributes to make it easier to display.
 */
export interface AwardedLanesApi
  extends UseGetReturn<ShipperRatesResponse, unknown, GetShipperRatesQueryParams, unknown> {
  /**
   * Returns a list of Shipper Rates that have calculated additional attributes
   */
  augmentedShipperRates: ShipperRateObjectAugmented[]
  /**
   * Updates the updated by field on the user for a given rate
   */
  updateUserOnSingleRate: (
    updatedRate: ShipperRateObjectAugmented,
    user?: Partial<INameSearchResult>
  ) => ShipperRateObjectAugmented
  /**
   * Updates the updated by field on the user for a given rate and updates the augmented list, used if you dont want to refetch the list
   */
  updateUserOnRate: (
    updatedRate: ShipperRateObjectAugmented,
    user?: Partial<INameSearchResult>
  ) => void
  /**
   * Updates the job status for a given rate, used if you dont want to refetch the list
   */
  updateJobStatus: (
    updatedRate: ShipperRateObjectAugmented,
    jobStatus?: ShipperRateObjectAugmented['sourceStatus']
  ) => void
  /**
   * Clears the list of Augmented Rates, and puts the hook in a loading state
   */
  clearRates: () => void
  /**
   * Calculates additional attributes on the Shipper Rate to make it easier to reference and display the data
   * Fields like loadType, pickupCity, deliveryCity are created so these do not need to be redone every time.
   */
  augmentRate: (rate: ShipperRateObject) => ShipperRateObjectAugmented
}

export const perPage = 50
export const initialPageParams = {
  pageCount: 0,
  perPage,
  sortField: IShipperRatesSortByEnum.start_date,
  sortOrder: 'asc',
}
export const initialQueryParams = {
  currentPage: 1,
  showPagination: false,
  pageTotalCount: 0,
  ...initialPageParams,
  filters: {
    includes: ['sourced_carrier_rates', 'carrier_sales_rep', 'sourced_carrier_rate_count'],
    shouldSource: true,
    assignedToMe: true,
    sourceStatus: ['deving'],
  },
  sortField: IShipperRatesSortByEnum.start_date,
  sortOrder: 'asc',
}

export const defaultOptions: UseGetShipperRatesProps = {
  queryParamStringifyOptions: {
    encodeValuesOnly: true,
    arrayFormat: 'brackets',
  },
  queryParams: initialQueryParams,
  lazy: true,
}

export const augmentRate = (rate: ShipperRateObject): ShipperRateObjectAugmented => {
  const {
    carrierSalesRep,
    delivery,
    endDate,
    isDeliveryDropTrailer,
    isPickupDropTrailer,
    pickup,
    startDate,
  } = rate || {}
  const { firstName, lastName } = carrierSalesRep || {}

  const startAt = moment(startDate).format()
  const endAt = endDate ? moment(endDate).format() : ''
  const loadType = getLoadType(isPickupDropTrailer, isDeliveryDropTrailer)

  const monthDuration = moment(endAt).diff(moment(startAt), 'months')

  const pickupCity =
    (pickup?.city && pickup?.stateCode
      ? `${capitalize(pickup?.city)}, ${pickup?.stateCode}`
      : pickup?.postalCode) || ''
  const deliveryCity =
    (delivery?.city && delivery?.stateCode
      ? `${capitalize(delivery?.city)}, ${delivery?.stateCode}`
      : delivery?.postalCode) || ''

  const salesRepName = carrierSalesRep ? `${firstName} ${lastName}` : 'Unassigned'
  const lane = `${pickupCity} - ${deliveryCity}`

  return {
    ...rate,
    id: rate.id || 0,
    loadType,
    monthDuration,
    pickupCity,
    deliveryCity,
    lane,
    salesRepName,
  }
}

export const useAwardedLanesApi = (): AwardedLanesApi => {
  // Creating a loading state here to prevent data flashing
  // The request's loading state does not take into account the completion of the useEffect to augment the data
  const [loading, setLoading] = useState<boolean>(false)
  const getShipperRates = useGetShipperRates(defaultOptions)
  const { data } = getShipperRates
  const [augmentedShipperRates, setAugmentedShipperRates] = useState<ShipperRateObjectAugmented[]>(
    []
  )

  const updateUserOnSingleRate = (
    rate: ShipperRateObjectAugmented,
    user?: Partial<INameSearchResult>
  ): ShipperRateObjectAugmented => {
    const { name } = user || {}
    const [firstName, lastName] = name?.split(' ') || ['', '']
    return {
      ...rate,
      carrierSalesRep: {
        id: Number(user?.id),
        firstName,
        lastName,
      },
      salesRepName: name || 'Unassigned',
    }
  }

  const updateUserOnRate = (
    updatedRate: ShipperRateObjectAugmented,
    user?: Partial<INameSearchResult>
  ) => {
    const updatedRates = augmentedShipperRates.map(rate => {
      if (rate.id === updatedRate.id) {
        return updateUserOnSingleRate(rate, user)
      }
      return rate
    })
    setAugmentedShipperRates(updatedRates)
  }

  const updateJobStatus = (
    updatedRate: ShipperRateObjectAugmented,
    jobStatus?: ShipperRateObjectAugmented['sourceStatus']
  ) => {
    const updatedRates = augmentedShipperRates.map(rate => {
      if (rate.id === updatedRate.id) {
        return {
          ...rate,
          sourceStatus: jobStatus,
        }
      }
      return rate
    })
    setAugmentedShipperRates(updatedRates)
  }

  useEffect(() => {
    const { shipperRates } = data || {}
    if (shipperRates) {
      const rates = shipperRates.map(augmentRate)
      setAugmentedShipperRates(rates)
    }
    setLoading(false)
  }, [data])

  const clearRates = () => {
    setAugmentedShipperRates([])
    setLoading(true)
  }

  return {
    ...getShipperRates,
    augmentedShipperRates,
    updateUserOnSingleRate,
    updateUserOnRate,
    updateJobStatus,
    loading,
    clearRates,
    augmentRate,
  }
}
